import React from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface StatTileProps {
    value: React.ReactNode;
    label: string;
    /** Small faded glyph in the top-right corner. */
    icon?: React.ReactNode;
    /** Optional second line under the value (e.g. a unit or breakdown). */
    sub?: string;
    /** Smaller, wrapping value -- for text values like a sport name. */
    compact?: boolean;
}

// The KPI tile used on the history page (and reusable elsewhere). Matches the
// FeedCard visual language: outlined, rounded, left-aligned, with a monospace
// uppercase caption like the card's date/stats rows.
function StatTile({ value, label, icon, sub, compact }: StatTileProps) {
    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                p: '14px 16px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                textAlign: 'left',
            }}
        >
            {icon && (
                <Box
                    aria-hidden
                    sx={{ position: 'absolute', top: 10, right: 12, color: 'text.disabled', '& svg': { fontSize: 20 } }}
                >
                    {icon}
                </Box>
            )}
            <Typography
                component="p"
                sx={{
                    fontSize: compact ? '1.05rem' : '1.75rem',
                    fontWeight: compact ? 600 : 700,
                    lineHeight: compact ? 1.25 : 1.1,
                    letterSpacing: '-0.01em',
                    fontVariantNumeric: 'tabular-nums',
                    m: 0,
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                }}
            >
                {value}
            </Typography>
            {sub && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: '-2px' }}>
                    {sub}
                </Typography>
            )}
            <Typography
                component="p"
                sx={{
                    mt: 0.5,
                    fontFamily: 'monospace',
                    fontSize: '0.68rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'primary.main',
                    m: 0,
                }}
            >
                {label}
            </Typography>
        </Card>
    );
}

export default StatTile;
